import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { MAIN_APP_NAME } from '../../config';
import { useResendActivation } from '../../hooks/useResendActivation';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  card: {
    maxWidth: 500,
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3)
  },
  logo: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    marginBottom: theme.spacing(2)
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  icon: {
    fontSize: 60,
    color: theme.palette.primary.main
  },
  successIcon: {
    fontSize: 60,
    color: theme.palette.success.main
  },
  form: {
    marginTop: theme.spacing(2)
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  submitButton: {
    marginTop: theme.spacing(1)
  },
  message: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center'
  }
}));

const ResendActivation = () => {
  const classes = useStyles();
  const { resendActivation, isLoading } = useResendActivation();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    const result = await resendActivation(email);
    
    if (result.success) {
      setEmailSent(true);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <img
              className={classes.logo}
              alt={MAIN_APP_NAME}
              src="/images/logos/logo.png"
            />
            <Typography variant="h3" color="textPrimary" gutterBottom>
              Resend Activation Email
            </Typography>
          </div>

          {!emailSent ? (
            <>
              <div className={classes.iconContainer}>
                <EmailIcon className={classes.icon} />
              </div>
              <Typography
                color="textSecondary"
                variant="body1"
                align="center"
                gutterBottom>
                Didn't receive your activation email? Enter your email address
                below and we'll send you a new activation link.
              </Typography>
              <form className={classes.form} onSubmit={handleSubmit}>
                <TextField
                  className={classes.textField}
                  error={Boolean(error)}
                  fullWidth
                  helperText={error}
                  label="Email address"
                  name="email"
                  onChange={handleEmailChange}
                  type="email"
                  value={email}
                  variant="outlined"
                  disabled={isLoading}
                />
                <Button
                  className={classes.submitButton}
                  color="primary"
                  disabled={isLoading || !email}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  {isLoading ? (
                    <>
                      <CircularProgress size={24} color="inherit" />
                      &nbsp; Sending...
                    </>
                  ) : (
                    'Resend Activation Email'
                  )}
                </Button>
              </form>
              <Box className={classes.message}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Note:</strong> Activation links are valid for 24
                  hours. If your link has expired, you can request a new one.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <div className={classes.iconContainer}>
                <CheckCircleIcon className={classes.successIcon} />
              </div>
              <Typography variant="h4" align="center" gutterBottom>
                Email Sent!
              </Typography>
              <Typography color="textSecondary" variant="body1" align="center">
                We've sent a new activation link to <strong>{email}</strong>.
                Please check your inbox and click the activation link to
                activate your account.
              </Typography>
              <Box className={classes.message}>
                <Typography variant="body2" color="textSecondary">
                  Didn't receive the email? Check your spam folder or try
                  again in a few minutes.
                </Typography>
              </Box>
              <Button
                className={classes.submitButton}
                color="default"
                fullWidth
                size="medium"
                variant="outlined"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}>
                Send to Different Email
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResendActivation;

